import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useUser } from 'context/user/User'
import { useAuth } from 'context/auth/Auth'
import userApi from 'api/requests/user'
import DynamicProfileBackground from 'components/commons/DynamicProfileBackground'
import DynamicProfileImage from 'components/commons/DynamicProfileImage'
import Loader from 'components/_UI/Loader'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import { Pencil, ChevronUp } from 'lucide-react'

export default function Profile({ handleEditUser = () => false, onEditClick, isEditing }) {
    const { user, setUser } = useUser()
    const { isAuth } = useAuth()
    const [loadUser, setLoadUser] = useState(false)

    useEffect(() => {
        if (!user?.id && isAuth) getMeData()
    }, [])

    async function getMeData() {
        setLoadUser(true)
        try {
            const { data } = await userApi.me()
            setUser(data)
        } catch (error) {
            setUser(false)
        } finally {
            setLoadUser(false)
        }
    }

    function handleEditClick() {
        if (onEditClick) return onEditClick()
        handleEditUser(user)
    }

    if (!user || loadUser) {
        return (
            <Card>
                <LoaderContainer><Loader /></LoaderContainer>
            </Card>
        )
    }

    return (
        <Card>
            <BannerSection>
                <DynamicProfileBackground img={user?.background_photo} size='140px' />
                <AvatarRing>
                    <DynamicProfileImage
                        img={user?.avatar}
                        word={user?.first_name}
                        size='80px'
                    />
                </AvatarRing>
            </BannerSection>

            <CardBody>
                <InfoRow>
                    <UserInfo>
                        <FullName>{user?.first_name} {user?.last_name}</FullName>
                        <Username>@{user?.username}</Username>
                        {user?.bio && <Bio>{user.bio}</Bio>}
                    </UserInfo>

                    <EditButton onClick={handleEditClick} isEditing={isEditing}>
                        {isEditing
                            ? <><ChevronUp size={13} /><span>Fechar</span></>
                            : <><Pencil size={13} /><span>Editar perfil</span></>
                        }
                    </EditButton>
                </InfoRow>
            </CardBody>
        </Card>
    )
}

const Card = styled.div`
    background: ${colors.neutralLight};
    box-shadow: 0 1px 4px rgba(0,0,0,0.08), 0 4px 20px rgba(0,0,0,0.04);

    @media screen and (min-width: ${breakpoints.sm}) {
        margin: 24px auto 0;
        max-width: 640px;
        border-radius: 14px;
        overflow: hidden;
    }
`

const LoaderContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
`

const BannerSection = styled.div`
    position: relative;
`

const AvatarRing = styled.div`
    position: absolute;
    bottom: -32px;
    left: 20px;
    width: 84px;
    height: 84px;
    border-radius: 50%;
    border: 3px solid ${colors.neutralLight};
    background: ${colors.neutralLight};
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
`

const CardBody = styled.div`
    padding: 40px 20px 20px;
`

const InfoRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`

const UserInfo = styled.div`
    flex: 1;
    min-width: 0;
`

const FullName = styled.h2`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 2px;
    line-height: 1.3;
`

const Username = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0 0 8px;
`

const Bio = styled.p`
    font-size: 14px;
    color: ${colors.primaryDashboard};
    margin: 0;
    line-height: 1.5;
`

const EditButton = styled.button`
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 14px;
    border-radius: 20px;
    border: 1.5px solid ${({ isEditing }) => isEditing ? colors.occasionalPurple : colors.nightLight};
    background: ${({ isEditing }) => isEditing ? `${colors.occasionalPurple}10` : 'transparent'};
    color: ${({ isEditing }) => isEditing ? colors.occasionalPurple : colors.night};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
    transition: all 0.2s ease;

    &:hover {
        border-color: ${colors.occasionalPurple};
        color: ${colors.occasionalPurple};
        background: ${colors.occasionalPurple}10;
    }

    span {
        display: none;

        @media screen and (min-width: ${breakpoints.xm}) {
            display: inline;
        }
    }
`
